import { useCallback, type RefObject } from "react"
import { debounce } from "radash"

import { useEffectOnce } from "./use-effect-once"

const scrollPosition: { [key: string]: number } = {}

const isDisableRecord: { [key: string]: boolean } = {}

interface UseScrollRestorationProps {
  key: string
  scrollableRef?: RefObject<HTMLElement | null>
  delay?: number
}

export const useScrollRestoration = ({
  key,
  scrollableRef,
  delay = 0,
}: UseScrollRestorationProps) => {
  const onScrollDebounced = debounce(
    {
      delay: 70,
    },
    (e: Event) => {
      if (isDisableRecord[key]) return

      const target = e.target as HTMLElement | null
      const scrollTop = target?.scrollTop ?? 0

      if (scrollTop === 0 && (scrollPosition?.[key] ?? 0) > 200) {
        return
      }

      if (scrollTop < 100) {
        scrollPosition[key] = 0
        return
      }

      scrollPosition[key] = scrollTop
    }
  )

  useEffectOnce(() => {
    let retryTime = 0
    const handler = () => {
      if (!scrollableRef?.current) {
        setTimeout(() => {
          retryTime++
          if (retryTime > 3) return
          handler()
        }, 100)

        return
      }

      scrollableRef.current?.addEventListener("scroll", onScrollDebounced, true)
    }

    handler()

    return () => {
      if (!scrollableRef?.current) return
      scrollableRef.current?.removeEventListener("scroll", onScrollDebounced)
    }
  })

  const restore = useCallback(
    (
      restoreScrollElement: HTMLElement | null | undefined,
      resetWithoutRestore: boolean = false
    ) => {
      if (!restoreScrollElement) return

      if (resetWithoutRestore) {
        window.history.replaceState({}, document.title)
        scrollPosition[key] = 0
        restoreScrollElement.scrollTop = 0
        return
      }

      const targetTop = scrollPosition[key] ?? 0
      if (targetTop <= 0) return

      isDisableRecord[key] = true

      const restoreInstantly = () => {
        const maxTop = Math.max(
          0,
          restoreScrollElement.scrollHeight - restoreScrollElement.clientHeight
        )

        restoreScrollElement.scrollTop = Math.min(targetTop, maxTop)

        requestAnimationFrame(() => {
          isDisableRecord[key] = false
        })
      }

      if (delay > 0) {
        setTimeout(() => {
          requestAnimationFrame(restoreInstantly)
        }, delay)
        return
      }

      requestAnimationFrame(restoreInstantly)
    },
    [key, delay]
  )

  return restore
}
